import { makeAutoObservable, runInAction } from 'mobx';
import { settingsApi } from '../api/settings_api';
import type { UserSettings, UserProfile, EmailPreferences, AdminUserSettings } from '../types';

export class SettingsStore {
  settings: UserSettings | null = null;
  loading = false;
  saving = false;
  error: string | null = null;

  // For admin viewing/editing other users
  adminSettings: Map<string, AdminUserSettings> = new Map();
  adminLoading: Map<string, boolean> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  async fetchSettings(): Promise<void> {
    if (this.loading) return;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const settings = await settingsApi.getSettings();
      runInAction(() => {
        this.settings = settings;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load settings';
      });
    }
  }

  async updateSettings(data: Partial<UserProfile & EmailPreferences>): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.error = null;
    });

    try {
      const settings = await settingsApi.updateSettings(data);
      runInAction(() => {
        this.settings = settings;
        this.saving = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.error = err instanceof Error ? err.message : 'Failed to save settings';
      });
      return false;
    }
  }

  async fetchAdminSettings(userUuid: string): Promise<void> {
    if (this.adminLoading.get(userUuid)) return;

    runInAction(() => {
      this.adminLoading.set(userUuid, true);
    });

    try {
      const settings = await settingsApi.getAdminSettings(userUuid);
      runInAction(() => {
        this.adminSettings.set(userUuid, settings);
        this.adminLoading.set(userUuid, false);
      });
    } catch {
      runInAction(() => {
        this.adminLoading.set(userUuid, false);
      });
    }
  }

  async updateAdminSettings(
    userUuid: string,
    data: Partial<UserProfile & EmailPreferences>
  ): Promise<boolean> {
    try {
      const settings = await settingsApi.updateAdminSettings(userUuid, data);
      runInAction(() => {
        this.adminSettings.set(userUuid, settings);
      });
      return true;
    } catch {
      return false;
    }
  }

  clearError(): void {
    this.error = null;
  }
}

export const settingsStore = new SettingsStore();
